import {
  type AfterViewInit,
  Component,
  ElementRef,
  inject,
  type OnDestroy,
  type OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { SettingsService, type ThemeString } from '../settings.service';
import {
  type ListFilter,
  type SearchResult,
  TaskService,
} from '../task.service';

export type SearchDialogComponentData = {
  orgId: string;
  spaceId: string;
  listContext: { listId: string; listFilter: ListFilter }[];
};

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  standalone: false,
})
export class SearchComponent implements OnInit, AfterViewInit, OnDestroy {
  private dialogRef = inject(MatDialogRef<SearchDialogComponentData>);
  private readonly data: SearchDialogComponentData = inject(MAT_DIALOG_DATA);
  private router = inject(Router);
  private taskService = inject(TaskService);
  private settingsService = inject(SettingsService);

  orgId: string;
  spaceId: string;
  listContext: { listId: string; listFilter: ListFilter }[];

  searchControl = new FormControl<string>('', { nonNullable: true });

  currentValue = '';

  @ViewChild('searchInput')
  searchInput: ElementRef<HTMLTextAreaElement>;

  @ViewChild(MatAutocompleteTrigger)
  searchAutocomplete: MatAutocompleteTrigger;

  searchUnderway?: Subscription = undefined;
  searchResultsInContext?: SearchResult[] = undefined;
  searchResultsNotInContext?: SearchResult[] = undefined;

  themeClass: ThemeString;

  subscriptions = new Subscription();

  constructor() {
    this.orgId = this.data.orgId;
    this.spaceId = this.data.spaceId;
    this.listContext = this.data.listContext;
  }

  ngOnInit(): void {
    this.searchControl.valueChanges.subscribe((value) => {
      // Intercept selecting the object
      if (typeof value !== 'string') {
        this.searchControl.setValue(this.currentValue);
        return;
      }
      this.currentValue = value;
      if (this.searchUnderway || value.length > 2) this.search();
    });

    const themeSubscription = this.settingsService.themeObservable.subscribe(
      (theme) => (this.themeClass = theme)
    );
    this.subscriptions.add(themeSubscription);
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.searchInput.nativeElement.focus(), 200);
  }

  search() {
    this.searchResultsInContext = undefined;
    this.searchResultsNotInContext = undefined;
    this.cancelCurrentSearch();

    const query = this.searchControl.value.trim();

    if (query.length === 0) return;

    const subject = this.taskService.searchTasks({
      query,
      orgId: this.orgId,
      spaceId: this.spaceId,
      listContext: this.listContext,
    });

    this.searchUnderway = subject.subscribe((results) => {
      this.searchResultsInContext = results.filter((r) => r.inCurrentContext);
      this.searchResultsNotInContext = results.filter(
        (r) => !r.inCurrentContext
      );
      this.cancelCurrentSearch();
    });

    this.subscriptions.add(this.searchUnderway);
  }

  clear() {
    this.searchControl.setValue('');
    this.searchResultsInContext = undefined;
    this.searchResultsNotInContext = undefined;
    this.cancelCurrentSearch();
  }

  cancelCurrentSearch() {
    if (!this.searchUnderway) return;
    this.searchUnderway.unsubscribe();
    this.searchUnderway = undefined;
  }

  selectResult(result: SearchResult) {
    this.searchResultsInContext = undefined;
    this.searchResultsNotInContext = undefined;
    this.cancelCurrentSearch();

    const { orgId, taskId } = result;
    if (!result.inCurrentContext) {
      this.router.navigate(['/org', orgId, 'task', taskId]);
      return;
    }

    this.taskService.scrollTaskIntoView(result);
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
